import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { admin } from '../../lib/api';
import type { EventRsvp, Player, TeamEvent, TeamEventStatus, TeamEventType, TeamUniform } from '../../types';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Checkbox, CheckboxField } from '../../ui/checkbox';
import { ErrorMessage, Field, Label } from '../../ui/fieldset';
import { Heading } from '../../ui/heading';
import { Input } from '../../ui/input';
import { Select } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import { Text } from '../../ui/text';
import { Panel } from '../../components/PageChrome';
import { LocationMap, LocationPinPicker } from '../../components/LocationMap';

const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalInput = (value: string) => new Date(value).toISOString();

const recurrenceLabel = (event: TeamEvent) => {
  if (!event.recurrence) return undefined;
  const frequency = event.recurrence.frequency.toLowerCase();
  const until = event.recurrence.until ? ` until ${new Date(event.recurrence.until).toLocaleDateString()}` : '';
  const count = event.recurrenceCount ? ` · occurrence ${(event.recurrenceIndex ?? 0) + 1} of ${event.recurrenceCount}` : '';
  return `Repeats ${frequency}${until}${count}`;
};

interface FormState {
  type: TeamEventType;
  title: string;
  startAt: string;
  endAt: string;
  arrivalAt: string;
  warmupAt: string;
  warmupLocationName: string;
  warmupAddress: string;
  warmupLatitude: number | undefined;
  warmupLongitude: number | undefined;
  locationName: string;
  address: string;
  uniformId: string;
  notes: string;
  status: TeamEventStatus;
  rsvpEnabled: boolean;
  notifyTeam: boolean;
}

const fromEvent = (event: TeamEvent): FormState => ({
  type: event.type,
  title: event.title,
  startAt: toLocalInput(event.startAt),
  endAt: event.endAt ? toLocalInput(event.endAt) : '',
  arrivalAt: event.arrivalAt ? toLocalInput(event.arrivalAt) : '',
  warmupAt: event.warmupAt ? toLocalInput(event.warmupAt) : '',
  warmupLocationName: event.warmupLocationName ?? '',
  warmupAddress: event.warmupAddress ?? '',
  warmupLatitude: event.warmupLatitude,
  warmupLongitude: event.warmupLongitude,
  locationName: event.locationName ?? '',
  address: event.address ?? '',
  uniformId: event.uniformId ?? '',
  notes: event.notes ?? '',
  status: event.status,
  rsvpEnabled: event.rsvpEnabled,
  notifyTeam: false,
});

export default function EventDetails() {
  const { teamId, eventId } = useParams<{ teamId: string; eventId: string }>();
  const [event, setEvent] = useState<TeamEvent>();
  const [uniforms, setUniforms] = useState<TeamUniform[]>([]);
  const [form, setForm] = useState<FormState>();
  const [matrix, setMatrix] = useState<Array<{ player: Player; status: EventRsvp['status']; rsvps: EventRsvp[] }>>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [savedAt, setSavedAt] = useState<string>();

  const load = async () => {
    if (!teamId || !eventId) return;
    const [e, r, u] = await Promise.all([
      admin.getEvent(teamId, eventId),
      admin.listEventRsvps(teamId, eventId),
      admin.listUniforms(teamId),
    ]);
    setEvent(e);
    setForm(fromEvent(e));
    setMatrix(r.matrix);
    setUniforms(u.uniforms);
  };

  useEffect(() => {
    void load().catch((err) => setError(err instanceof Error ? err.message : String(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, eventId]);

  const persist = async (nextForm: FormState) => {
    if (!teamId || !eventId) return;
    setSaving(true);
    setError(undefined);
    try {
      await admin.updateEvent(teamId, eventId, {
        title: nextForm.title.trim(),
        startAt: fromLocalInput(nextForm.startAt),
        endAt: nextForm.endAt ? fromLocalInput(nextForm.endAt) : null,
        arrivalAt: nextForm.arrivalAt ? fromLocalInput(nextForm.arrivalAt) : null,
        warmupAt: nextForm.warmupAt ? fromLocalInput(nextForm.warmupAt) : null,
        warmupLocationName: nextForm.warmupLocationName.trim() || null,
        warmupAddress: nextForm.warmupAddress.trim() || null,
        warmupLatitude: nextForm.warmupLatitude ?? null,
        warmupLongitude: nextForm.warmupLongitude ?? null,
        locationName: nextForm.locationName.trim() || null,
        address: nextForm.address.trim() || null,
        uniformId: nextForm.uniformId || null,
        notes: nextForm.notes.trim() || null,
        status: nextForm.status,
        rsvpEnabled: nextForm.rsvpEnabled,
        notifyTeam: nextForm.notifyTeam,
      });
      await load();
      setSavedAt(new Date().toLocaleTimeString());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    await persist(form);
  };

  const setStatus = (status: TeamEventStatus) => {
    if (!form) return;
    setForm({ ...form, status, notifyTeam: true });
  };

  const quickStatus = async (status: TeamEventStatus) => {
    if (!form) return;
    if (
      status === 'CANCELLED' &&
      !confirm(`Cancel "${form.title}"? Team members may be notified.`)
    ) {
      return;
    }
    const nextForm = { ...form, status, notifyTeam: true };
    setForm(nextForm);
    await persist(nextForm);
  };

  if (!form || !event) {
    return <div>{error ? <Text className="text-red-400">{error}</Text> : <Text>Loading event...</Text>}</div>;
  }

  const uniformOptions = uniforms.filter(
    (uniform) => uniform.active || uniform.uniformId === form.uniformId,
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Heading level={2}>{event.title}</Heading>
          <div className="mt-2 flex flex-wrap gap-2">
            <Badge color={event.type === 'GAME' ? 'red' : event.type === 'PRACTICE' ? 'sky' : 'zinc'}>
              {event.type.replace('_', ' ')}
            </Badge>
            <Badge color={event.status === 'SCHEDULED' ? 'lime' : event.status === 'CANCELLED' ? 'rose' : 'amber'}>
              {event.status}
            </Badge>
            {event.rsvpEnabled ? <Badge color="amber">RSVP</Badge> : null}
            {event.uniform ? <Badge color="zinc">{event.uniform.name}</Badge> : null}
          </div>
          {recurrenceLabel(event) ? <Text className="mt-2 text-sm">{recurrenceLabel(event)}</Text> : null}
        </div>
        <Button href={`/admin/teams/${teamId}/calendar`} outline>
          Calendar
        </Button>
      </div>

      <form onSubmit={save} className="space-y-6">
        <Panel className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field>
              <Label>Type</Label>
              <Select value={form.type} disabled>
                <option value="GAME">Game</option>
                <option value="PRACTICE">Practice</option>
                <option value="TEAM_EVENT">Team event</option>
              </Select>
            </Field>
            <Field className="sm:col-span-2">
              <Label>Title</Label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </Field>
            <Field>
              <Label>Start</Label>
              <Input required type="datetime-local" value={form.startAt} onChange={(e) => setForm({ ...form, startAt: e.target.value })} />
            </Field>
            <Field>
              <Label>End</Label>
              <Input type="datetime-local" value={form.endAt} onChange={(e) => setForm({ ...form, endAt: e.target.value })} />
            </Field>
            <Field>
              <Label>Arrival</Label>
              <Input type="datetime-local" value={form.arrivalAt} onChange={(e) => setForm({ ...form, arrivalAt: e.target.value })} />
            </Field>
            <Field>
              <Label>Uniform</Label>
              <Select value={form.uniformId} onChange={(e) => setForm({ ...form, uniformId: e.target.value })}>
                <option value="">— None —</option>
                {uniformOptions.map((uniform) => (
                  <option key={uniform.uniformId} value={uniform.uniformId}>
                    {uniform.name}{uniform.active ? '' : ' (inactive)'}
                  </option>
                ))}
              </Select>
            </Field>
            <Field>
              <Label>Warmup time</Label>
              <Input type="datetime-local" value={form.warmupAt} onChange={(e) => setForm({ ...form, warmupAt: e.target.value })} />
            </Field>
            <Field>
              <Label>Warmup place</Label>
              <Input value={form.warmupLocationName} onChange={(e) => setForm({ ...form, warmupLocationName: e.target.value })} />
            </Field>
            <Field>
              <Label>Location</Label>
              <Input value={form.locationName} onChange={(e) => setForm({ ...form, locationName: e.target.value })} />
            </Field>
            <Field className="sm:col-span-2">
              <Label>Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </Field>
            <Field className="sm:col-span-3">
              <Label>Warmup address</Label>
              <Input value={form.warmupAddress} onChange={(e) => setForm({ ...form, warmupAddress: e.target.value })} />
            </Field>
          </div>
          <Field className="space-y-2">
            <Label>Warmup pin</Label>
            <LocationPinPicker
              value={{ latitude: form.warmupLatitude, longitude: form.warmupLongitude }}
              onChange={(pin) => setForm({ ...form, warmupLatitude: pin.latitude, warmupLongitude: pin.longitude })}
            />
            {form.warmupLatitude !== undefined && form.warmupLongitude !== undefined ? (
              <div className="flex flex-wrap items-center gap-3">
                <Text className="font-mono text-xs">{form.warmupLatitude}, {form.warmupLongitude}</Text>
                <Button
                  type="button"
                  outline
                  onClick={() => setForm({ ...form, warmupLatitude: undefined, warmupLongitude: undefined })}
                >
                  Clear pin
                </Button>
              </div>
            ) : null}
          </Field>
          <Field>
            <Label>Notes</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field>
              <Label>Status</Label>
              <Select value={form.status} onChange={(e) => setStatus(e.target.value as TeamEventStatus)}>
                <option value="SCHEDULED">Scheduled</option>
                <option value="POSTPONED">Postponed</option>
                <option value="CANCELLED">Cancelled</option>
                <option value="COMPLETED">Completed</option>
              </Select>
            </Field>
            <div className="space-y-3 pt-7">
              <CheckboxField>
                <Checkbox checked={form.rsvpEnabled} onChange={(checked) => setForm({ ...form, rsvpEnabled: checked })} />
                <Label>Collect RSVP responses</Label>
              </CheckboxField>
              <CheckboxField>
                <Checkbox checked={form.notifyTeam} onChange={(checked) => setForm({ ...form, notifyTeam: checked })} />
                <Label>Notify team on save</Label>
              </CheckboxField>
            </div>
          </div>
        </Panel>

        {error ? <ErrorMessage>{error}</ErrorMessage> : null}

        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" color="dark/zinc" disabled={saving}>
            {saving ? 'Saving...' : 'Save event'}
          </Button>
          <Button
            type="button"
            outline
            disabled={saving}
            onClick={() => void quickStatus('CANCELLED')}
          >
            Cancel event
          </Button>
          <Button
            type="button"
            outline
            disabled={saving}
            onClick={() => void quickStatus('POSTPONED')}
          >
            Postpone event
          </Button>
          {savedAt ? <Text className="text-xs text-zinc-500">Saved at {savedAt}</Text> : null}
        </div>
      </form>

      {event.locationName || event.address || event.warmupLocationName || event.warmupAddress || event.warmupLatitude !== undefined ? (
        <Panel className="space-y-5">
          <Heading level={3}>Locations</Heading>
          {event.locationName || event.address ? (
            <div className="space-y-2">
              <Text className="font-medium text-zinc-200">{[event.locationName, event.address].filter(Boolean).join(' - ')}</Text>
              <LocationMap locationName={event.locationName} address={event.address} />
            </div>
          ) : null}
          {event.warmupLocationName || event.warmupAddress || event.warmupLatitude !== undefined ? (
            <div className="space-y-2">
              <Text className="font-medium text-zinc-200">
                {event.warmupAt ? `${new Date(event.warmupAt).toLocaleString()} - ` : ''}
                {[event.warmupLocationName, event.warmupAddress].filter(Boolean).join(' - ') || 'Warmup pin'}
              </Text>
              <LocationMap
                locationName={event.warmupLocationName}
                address={event.warmupAddress}
                latitude={event.warmupLatitude}
                longitude={event.warmupLongitude}
              />
            </div>
          ) : null}
        </Panel>
      ) : null}

      <Panel>
        <Heading level={3}>RSVP matrix</Heading>
        {matrix.length === 0 ? (
          <Text className="mt-2 text-sm">No player RSVP responses yet.</Text>
        ) : (
          <ul className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {matrix.map((row) => (
              <li key={row.player.playerId} className="rounded border border-zinc-800 bg-zinc-950 p-3 text-sm">
                #{row.player.jerseyNumber} {row.player.name} - {row.status}
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
